import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { AdminNav } from "../components/AdminNav";
import { downloadText } from "../admin/logoAssets";
import { rasterizeSvgs, buildBorderedPdf, exportCombinedPdf } from "../admin/pdfExport";
import { buildTributeProspectusHtml, buildTributePacketHtml } from "../admin/tributeProspectus";
import { buildTributeInvoiceHtml } from "../admin/tributeInvoice";
import { buildLifeStudyProspectusHtml, buildLifeStudyPacketHtml } from "../admin/lifeStudyProspectus";
import { buildBiographyProposalHtml } from "../admin/biographyProposal";
import { buildServiceAgreementHtml } from "../admin/serviceAgreement";
import { buildLifeStudyInvoiceHtml, LS_INSTALMENTS } from "../admin/lifeStudyInvoice";
import { buildExtensionsInvoiceHtml, EXT_PRESETS } from "../admin/extensionsInvoice";
import { EXTENSION_PRICES } from "../admin/extensionPricing";
import { buildExtensionsProspectusHtml } from "../admin/extensionsProspectus";
import { buildOrgProspectusHtml } from "../admin/orgProspectus";

const DOC_GROUPS = [
  {
    id: "life-study",
    label: "Life Study",
    sublabel: "Client Facing",
    docs: [
      { id: "biography-proposal", label: "Biography Proposal" },
      { id: "service-agreement", label: "Service Agreement" },
      { id: "life-study-prospectus", label: "Life Study Prospectus" },
      { id: "org-prospectus", label: "Organisational Prospectus" },
      { id: "extensions-prospectus", label: "Extensions Prospectus" },
      { id: "life-study-invoice", label: "Life Study Invoice" },
      { id: "extensions-invoice", label: "Extensions Invoice" },
    ],
  },
  {
    id: "tribute",
    label: "Final Tribute",
    sublabel: "Client Facing",
    docs: [
      { id: "tribute-prospectus", label: "Tribute Prospectus" },
      { id: "tribute-invoice", label: "Tribute Invoice" },
    ],
  },
];

const slug = (s) =>
  (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Initials of the commissioning individual (drops articles / honorifics / "family").
const STOP_WORDS = new Set(["the", "family", "estate", "mr", "mrs", "ms", "dr", "prof", "and", "of"]);
const getInitials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter((w) => w && !STOP_WORDS.has(w.toLowerCase()))
    .map((w) => w[0].toUpperCase())
    .join("")
    .slice(0, 3);

const formatToday = () => {
  const d = new Date();
  return `${d.getDate()} ${d.toLocaleString("en-GB", { month: "long" })} ${d.getFullYear()}`;
};

/* ---------------------- Commission Profile (shared across docs) ---------------------- */
const EMPTY_PROFILE = {
  subjectFullName: "", subjectFirstName: "", familyName: "", subjectLocation: "",
  projectTitle: "", date: "", commencementDate: "", deliveryDate: "",
  totalInvestment: "", instalment: "", copies: "", additionalCopyPrice: "",
  pages: "", words: "", images: "", subjectInterviews: "", circleInterviews: "",
};

// House-standard full commission figures (names / dates / location stay client-specific).
const PROFILE_STANDARD = {
  totalInvestment: "R1,024,000",
  instalment: "R256,000",
  copies: "sixty",
  additionalCopyPrice: "R1,500",
  pages: "200 to 240",
  words: "60,000 to 70,000",
  images: "40 to 60",
  subjectInterviews: "fourteen (14)",
  circleInterviews: "sixteen (16)",
};

const loadProfile = () => {
  try {
    const raw = localStorage.getItem("rq_commission_profile");
    return raw ? { ...EMPTY_PROFILE, ...JSON.parse(raw) } : { ...EMPTY_PROFILE };
  } catch {
    return { ...EMPTY_PROFILE };
  }
};

// Returns only the non-empty profile values, keyed to a document's own field names.
const applyMap = (profile, map) => {
  const out = {};
  for (const [profKey, fieldKey] of Object.entries(map)) {
    const v = ((profile && profile[profKey]) || "").trim();
    if (v) out[fieldKey] = v;
  }
  return out;
};

const PROSPECTUS_MAP = {
  familyName: "familyName", subjectFullName: "subjectFullName", subjectFirstName: "subjectFirstName",
  date: "date", copies: "includedVolumes", additionalCopyPrice: "additionalCopyPrice",
  totalInvestment: "investment", instalment: "instalment",
};
const PROPOSAL_MAP = {
  projectTitle: "workingTitle", subjectFirstName: "subjectName", subjectFullName: "subjectFullName",
  subjectLocation: "subjectLocation", date: "proposalDate", copies: "printRun",
  totalInvestment: "fee", instalment: "instalment", pages: "pages", words: "words", images: "images",
};
const AGREEMENT_MAP = {
  projectTitle: "projectTitle", date: "proposalDate", subjectFullName: "subjectLegalName",
  commencementDate: "commencementDate", deliveryDate: "deliveryDate", words: "words",
  copies: "copies", additionalCopyPrice: "additionalCopyPrice", totalInvestment: "totalFee",
  instalment: "instalment", pages: "pages", images: "images",
  subjectInterviews: "subjectInterviews", circleInterviews: "circleInterviews",
};
const LS_INVOICE_MAP = {
  subjectFullName: "billedTo", projectTitle: "projectTitle", date: "agreementDate",
  totalInvestment: "totalFee", commencementDate: "commencementDate", deliveryDate: "deliveryDate",
};
const EXT_INVOICE_MAP = {
  subjectFullName: "billedTo", projectTitle: "projectTitle", date: "agreementDate",
};
const EXT_PROSPECTUS_MAP = {
  subjectFullName: "subjectFullName", familyName: "familyName", date: "date",
  totalInvestment: "lifeStudyFee", additionalCopyPrice: "additionalCopyPrice",
};
const ORG_PROSPECTUS_MAP = {
  familyName: "organisationName", date: "date",
};

// A small, separate profile for the Final Tribute family (its terms differ
// entirely from a Life Study, so it does not share the Commission Profile).
const EMPTY_TRIBUTE_PROFILE = {
  commissionerName: "", deceasedName: "", date: "",
};
const TRIBUTE_PROFILE_FIELDS = [
  { k: "commissionerName", label: "Commissioner / family", ph: "The Maluleke family" },
  { k: "deceasedName", label: "Full name of the deceased", ph: "Samson Josiah Maluleke" },
  { k: "date", label: "Date", ph: "18 July 2026" },
];
const TRIBUTE_PROSPECTUS_MAP = {
  commissionerName: "familyName", deceasedName: "deceasedName", date: "date",
};
const TRIBUTE_INVOICE_MAP = {
  commissionerName: "billedTo", deceasedName: "deceasedName",
};

const loadTributeProfile = () => {
  try {
    const raw = localStorage.getItem("rq_tribute_profile");
    return raw ? { ...EMPTY_TRIBUTE_PROFILE, ...JSON.parse(raw) } : { ...EMPTY_TRIBUTE_PROFILE };
  } catch {
    return { ...EMPTY_TRIBUTE_PROFILE };
  }
};

const PROFILE_FIELDS = [
  { k: "subjectFullName", label: "Subject's full name", ph: "Nthobi Angel Moahloli" },
  { k: "subjectFirstName", label: "Subject's first name", ph: "Nthobi" },
  { k: "familyName", label: "Family / prepared for", ph: "The Moahloli family" },
  { k: "subjectLocation", label: "Location", ph: "Cape Town" },
  { k: "projectTitle", label: "Working / project title", ph: "Four Seasons in One Day" },
  { k: "date", label: "Document date", ph: "7 June 2026" },
  { k: "commencementDate", label: "Commencement date", ph: "1 January 2027" },
  { k: "deliveryDate", label: "Delivery date", ph: "On or about 31 December 2027" },
  { k: "totalInvestment", label: "Total investment", ph: "R1,024,000" },
  { k: "instalment", label: "Per instalment (x4)", ph: "R256,000" },
  { k: "copies", label: "Included copies", ph: "sixty" },
  { k: "additionalCopyPrice", label: "Additional copy price", ph: "R1,500" },
  { k: "pages", label: "Pages", ph: "200 to 240" },
  { k: "words", label: "Word count", ph: "60,000 to 70,000" },
  { k: "images", label: "Photographs", ph: "40 to 60" },
  { k: "subjectInterviews", label: "Interviews with Subject", ph: "fourteen (14)" },
  { k: "circleInterviews", label: "Interviews with circle", ph: "sixteen (16)" },
];

const CommissionProfile = ({ profile, setProfile }) => {
  const [open, setOpen] = useState(true);
  const set = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));
  const filled = Object.values(profile).filter((v) => (v || "").trim()).length;
  return (
    <div className="border border-[#7A2E2E]/40 bg-white/50" data-testid="commission-profile">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="commission-profile-toggle"
        className="w-full flex items-center justify-between px-5 py-4"
      >
        <span className="text-[12px] uppercase tracking-[0.28em] text-[#7A2E2E]">Commission Profile</span>
        <span className="text-[11px] italic text-[#6E6A5E]">
          {filled ? `${filled} field${filled > 1 ? "s" : ""} set` : "empty"} · {open ? "hide" : "show"}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-6">
          <p className="text-[12px] italic text-[#6E6A5E] mb-5 max-w-[72ch]">
            Fill this once. These values pre-fill the matching fields in the Biography Proposal, Life Study
            Prospectus, and Service Agreement, so the subject, dates, and figures never disagree across the
            paperwork. You can still adjust any field within a document. Saved in this browser.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROFILE_FIELDS.map((f) => (
              <Field
                key={f.k}
                id={`cp-${f.k}`}
                label={f.label}
                value={profile[f.k]}
                onChange={set(f.k)}
                placeholder={f.ph}
                testid={`commission-profile-${slug(f.k)}`}
              />
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button className={BTN_CLASS} data-testid="commission-profile-load-standard" onClick={() => setProfile((p) => ({ ...p, ...PROFILE_STANDARD }))}>
              Load standard commission
            </button>
            <button className={BTN_CLASS} data-testid="commission-profile-clear" onClick={() => setProfile({ ...EMPTY_PROFILE })}>
              Clear profile
            </button>
            <span className="text-[11px] italic text-[#6E6A5E]">
              "Load standard commission" fills the house-standard figures (investment, instalment, run, extents) in one tap; names, dates, and location stay yours to enter.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const TributeProfile = ({ profile, setProfile }) => {
  const [open, setOpen] = useState(true);
  const set = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));
  const filled = Object.values(profile).filter((v) => (v || "").trim()).length;
  return (
    <div className="border border-[#7A2E2E]/40 bg-white/50" data-testid="tribute-profile">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="tribute-profile-toggle"
        className="w-full flex items-center justify-between px-5 py-4"
      >
        <span className="text-[12px] uppercase tracking-[0.28em] text-[#7A2E2E]">Tribute Profile</span>
        <span className="text-[11px] italic text-[#6E6A5E]">
          {filled ? `${filled} field${filled > 1 ? "s" : ""} set` : "empty"} · {open ? "hide" : "show"}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-6">
          <p className="text-[12px] italic text-[#6E6A5E] mb-5 max-w-[72ch]">
            Fill this once. These values pre-fill the matching fields in the Tribute Prospectus and the Tribute
            Invoice, so the commissioner and the deceased never disagree across the two. You can still adjust
            any field within a document. Saved in this browser.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {TRIBUTE_PROFILE_FIELDS.map((f) => (
              <Field
                key={f.k}
                id={`tp-${f.k}`}
                label={f.label}
                value={profile[f.k]}
                onChange={set(f.k)}
                placeholder={f.ph}
                testid={`tribute-profile-${slug(f.k)}`}
              />
            ))}
          </div>
          <div className="mt-5">
            <button className={BTN_CLASS} data-testid="tribute-profile-clear" onClick={() => setProfile({ ...EMPTY_TRIBUTE_PROFILE })}>
              Clear profile
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDocuments = () => {
  const navigate = useNavigate();
  const [group, setGroup] = useState("life-study");
  const [doc, setDoc] = useState("life-study-prospectus");
  const [profile, setProfile] = useState(loadProfile);
  const [tributeProfile, setTributeProfile] = useState(loadTributeProfile);
  const token = typeof window !== "undefined" ? localStorage.getItem("rq_admin_token") : null;

  useEffect(() => {
    if (!token) navigate("/admin");
  }, [token, navigate]);

  useEffect(() => {
    try {
      localStorage.setItem("rq_commission_profile", JSON.stringify(profile));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [profile]);

  useEffect(() => {
    try {
      localStorage.setItem("rq_tribute_profile", JSON.stringify(tributeProfile));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [tributeProfile]);

  return (
    <div className="min-h-screen bg-[#F4F1EA] pb-24" data-testid="admin-documents-page">
      <AdminNav active="documents" />
      <div className="mx-auto max-w-[900px] px-6 md:px-8 pt-14">
        <h1 className="text-[28px] md:text-[32px] italic text-[#1C1A17]">Client Documents</h1>
        <p className="mt-4 text-[14px] text-[#6E6A5E] max-w-[62ch]">
          Personalised, print-ready papers to hand to a family or client in person, or to send
          digitally. Fill in the details, preview, then download and print to PDF from your browser.
        </p>

        <div className="mt-10">
          {group === "tribute" ? (
            <TributeProfile profile={tributeProfile} setProfile={setTributeProfile} />
          ) : (
            <CommissionProfile profile={profile} setProfile={setProfile} />
          )}
        </div>

        <div className="mt-10">
          {/* Primary category tabs, client-facing document families */}
          <div className="flex flex-wrap gap-4" data-testid="documents-groups">
            {DOC_GROUPS.map((g) => {
              const activeGroup = group === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => {
                    setGroup(g.id);
                    setDoc(g.docs[0].id);
                  }}
                  data-testid={`documents-group-${g.id}`}
                  className={`px-6 py-4 text-left border transition-colors ${
                    activeGroup
                      ? "border-[#7A2E2E] bg-white"
                      : "border-[#A8A296]/50 hover:border-[#7A2E2E] bg-transparent"
                  }`}
                >
                  <div
                    className={`text-[12px] uppercase tracking-[0.28em] ${
                      activeGroup ? "text-[#7A2E2E]" : "text-[#1C1A17]"
                    }`}
                  >
                    {g.label}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.24em] text-[#A8A296]">
                    {g.sublabel}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Secondary tabs, the documents within the selected family (wraps) */}
          <div
            className="mt-8 flex flex-wrap gap-x-6 gap-y-3 border-b"
            style={{ borderColor: "rgba(168,162,150,0.4)" }}
            data-testid="documents-tabs"
          >
            {(DOC_GROUPS.find((g) => g.id === group)?.docs || []).map((d) => (
              <button
                key={d.id}
                onClick={() => setDoc(d.id)}
                data-testid={`documents-tab-${d.id}`}
                className={`pb-3 text-[11px] uppercase tracking-[0.28em] transition-colors ${
                  doc === d.id
                    ? "text-[#7A2E2E] border-b-2 border-[#7A2E2E]"
                    : "text-[#A8A296] hover:text-[#1C1A17]"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12">
          {doc === "biography-proposal" && <BiographyProposalDoc profile={profile} />}
          {doc === "service-agreement" && <ServiceAgreementDoc profile={profile} />}
          {doc === "life-study-prospectus" && <LifeStudyProspectusDoc profile={profile} />}
          {doc === "org-prospectus" && <OrgProspectusDoc profile={profile} />}
          {doc === "extensions-prospectus" && <ExtensionsProspectusDoc profile={profile} />}
          {doc === "life-study-invoice" && <LifeStudyInvoiceDoc profile={profile} />}
          {doc === "extensions-invoice" && <ExtensionsInvoiceDoc profile={profile} />}
          {doc === "tribute-prospectus" && <TributeProspectusDoc profile={tributeProfile} />}
          {doc === "tribute-invoice" && <TributeInvoiceDoc profile={tributeProfile} />}
        </div>
      </div>
    </div>
  );
};

/* ---------------------------- Shared UI primitives ---------------------------- */
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SendForSignaturePanel = ({ testidPrefix, docType, title, html }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!email.trim() || sending) return;
    setSending(true);
    setResult(null);
    try {
      const token = localStorage.getItem("rq_admin_token");
      const res = await fetch(`${API_BASE}/admin/signing-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          doc_type: docType,
          title,
          client_name: name.trim(),
          client_email: email.trim(),
          document_html: html,
          sign_base_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Could not send the document");
      }
      setResult({ ok: true, msg: `Sent to ${email.trim()}. It will return here, under Client Signatures, once signed.` });
      setEmail("");
      setName("");
    } catch (e) {
      setResult({ ok: false, msg: e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-[#7A2E2E]/40 bg-white/50 p-5" data-testid={`${testidPrefix}-signature-panel`}>
      <div className="text-[12px] uppercase tracking-[0.28em] text-[#7A2E2E] mb-2">Send for e-signature</div>
      <p className="text-[12px] italic text-[#6E6A5E] mb-4 max-w-[72ch]">
        Send this exact document to the client to review and sign electronically (by mouse or finger). Once they
        submit, the signed copy returns here, under Client Signatures, and you receive an email.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLASS} htmlFor={`${testidPrefix}-sign-name`}>Client name (optional)</label>
          <input id={`${testidPrefix}-sign-name`} type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="The Mokoena family" className={INPUT_CLASS} data-testid={`${testidPrefix}-sign-name`} />
        </div>
        <div>
          <label className={LABEL_CLASS} htmlFor={`${testidPrefix}-sign-email`}>Client email</label>
          <input id={`${testidPrefix}-sign-email`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@example.com" className={INPUT_CLASS} data-testid={`${testidPrefix}-sign-email`} />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-4">
        <button className={BTN_CLASS} onClick={send} disabled={sending || !email.trim()} data-testid={`${testidPrefix}-send-signature`}>
          {sending ? "Sending..." : "Send for signature"}
        </button>
        {result && (
          <span className={`text-[12px] italic ${result.ok ? "text-[#3E6E3E]" : "text-[#7A2E2E]"}`} data-testid={`${testidPrefix}-signature-result`}>
            {result.msg}
          </span>
        )}
      </div>
    </div>
  );
};

const INPUT_CLASS =
  "w-full border border-[#A8A296] bg-transparent px-4 py-3 min-h-[46px] text-[15px] outline-none transition-colors focus:border-[#7A2E2E] text-[#1C1A17] placeholder:text-[#A8A296] placeholder:italic rounded-none";
const LABEL_CLASS = "block text-[11px] uppercase tracking-[0.28em] text-[#7A2E2E] mb-2";
const BTN_CLASS =
  "text-[10px] uppercase tracking-[0.24em] border border-[#7A2E2E] text-[#7A2E2E] px-4 py-2 hover:bg-[#7A2E2E] hover:text-[#F4F1EA] transition-colors disabled:opacity-50";
const SMALL_BTN =
  "text-[10px] uppercase tracking-[0.2em] border border-[#A8A296] text-[#6E6A5E] px-3 py-2 hover:border-[#7A2E2E] hover:text-[#7A2E2E] transition-colors";

const Field = ({ id, label, value, onChange, placeholder, testid, wide }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <label className={LABEL_CLASS} htmlFor={id}>{label}</label>
    <input
      id={id}
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={INPUT_CLASS}
      data-testid={testid}
    />
  </div>
);

const TextArea = ({ id, label, value, onChange, placeholder, testid, rows = 8 }) => (
  <div className="sm:col-span-2">
    <label className={LABEL_CLASS} htmlFor={id}>{label}</label>
    <textarea
      id={id}
      rows={rows}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`${INPUT_CLASS} resize-y leading-[1.7]`}
      data-testid={testid}
    />
  </div>
);

// Reusable panel shared by every client document: header, the caller's form
// fields, the PDF / open / HTML actions, and the live preview. Owns the PDF
// export pipeline (off-screen clone + SVG rasterisation + bordered A4).
const DocumentPanel = ({ testidPrefix, kicker, title, description, html, baseName, children, packetHtml, packetBaseName, packetLabel = "Download prospectus + sample (PDF)", signable = false, docType }) => {
  const [busy, setBusy] = useState(false);
  const [packetBusy, setPacketBusy] = useState(false);
  const iframeRef = useRef(null);
  const packetIframeRef = useRef(null);

  const downloadPacket = async () => {
    if (!packetIframeRef.current || packetBusy) return;
    setPacketBusy(true);
    try {
      await exportCombinedPdf({ iframe: packetIframeRef.current, filename: `${packetBaseName}.pdf` });
    } finally {
      setPacketBusy(false);
    }
  };

  const downloadPdf = async () => {
    const iframe = iframeRef.current;
    if (!iframe || busy) return;
    const doc = iframe.contentDocument;
    const liveSheet = doc && doc.querySelector(".sheet");
    if (!liveSheet) return;
    setBusy(true);
    let clone = null;
    try {
      await (doc.fonts ? doc.fonts.ready : Promise.resolve());
      const liveW = liveSheet.getBoundingClientRect().width;
      clone = liveSheet.cloneNode(true);
      clone.style.cssText += `position:absolute;left:-10000px;top:0;width:${liveW}px;margin:0;background:#ffffff;`;
      doc.body.appendChild(clone);
      await new Promise((r) => setTimeout(r, 30));
      await rasterizeSvgs(doc, clone);
      const canvas = await html2canvas(clone, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        windowWidth: liveW,
      });
      const sheetRect = clone.getBoundingClientRect();
      const pxPerCss = canvas.width / sheetRect.width;
      const links = Array.from(clone.querySelectorAll("a[href]")).map((a) => {
        const r = a.getBoundingClientRect();
        return {
          x: (r.left - sheetRect.left) * pxPerCss,
          y: (r.top - sheetRect.top) * pxPerCss,
          w: r.width * pxPerCss,
          h: r.height * pxPerCss,
          url: a.href,
        };
      });
      buildBorderedPdf(canvas, `${baseName}.pdf`, links);
    } finally {
      if (clone && clone.parentNode) clone.parentNode.removeChild(clone);
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid={`${testidPrefix}-doc`}>
      <div>
        <span className="block text-[11px] uppercase tracking-[0.3em] text-[#7A2E2E] mb-1">{kicker}</span>
        <h2 className="text-[22px] italic text-[#1C1A17]">{title}</h2>
        <p className="mt-2 text-[13px] leading-relaxed text-[#6E6A5E] max-w-[62ch]">{description}</p>
      </div>

      {children}

      <div className="flex flex-wrap gap-3">
        <button className={BTN_CLASS} data-testid={`${testidPrefix}-download-pdf`} onClick={downloadPdf} disabled={busy}>
          {busy ? "Preparing PDF..." : "Download as PDF"}
        </button>
        {packetHtml && (
          <button className={BTN_CLASS} data-testid={`${testidPrefix}-download-packet`} onClick={downloadPacket} disabled={packetBusy}>
            {packetBusy ? "Preparing packet..." : packetLabel}
          </button>
        )}
        <button
          className={BTN_CLASS}
          data-testid={`${testidPrefix}-open`}
          onClick={() => {
            const w = window.open("", "_blank");
            if (w) { w.document.write(html); w.document.close(); }
          }}
        >
          Open in New Tab
        </button>
        <button
          className={BTN_CLASS}
          data-testid={`${testidPrefix}-download`}
          onClick={() => downloadText(`${baseName}.html`, html, "text/html")}
        >
          Download HTML
        </button>
      </div>

      {signable && (
        <SendForSignaturePanel testidPrefix={testidPrefix} docType={docType} title={title} html={html} />
      )}

      <div className="border border-[#A8A296] bg-white" data-testid={`${testidPrefix}-preview`}>
        <iframe
          ref={iframeRef}
          title={title}
          srcDoc={html}
          className="w-full block"
          style={{ height: "1400px", border: "0" }}
          onLoad={(e) => {
            try {
              const d = e.target.contentDocument;
              if (d && d.body) e.target.style.height = d.body.scrollHeight + 40 + "px";
            } catch (_) {}
          }}
        />
      </div>

      {packetHtml && (
        <iframe
          ref={packetIframeRef}
          title={`${title} packet`}
          srcDoc={packetHtml}
          aria-hidden="true"
          tabIndex={-1}
          style={{ position: "absolute", left: "-10000px", top: 0, width: "800px", height: "8000px", border: "0" }}
        />
      )}
    </div>
  );
};

/* --------------------- The Raven's Quill, Service Agreement --------------------- */
const ServiceAgreementDoc = ({ profile }) => {
  const [fields, setFields] = useState({
    projectTitle: "",
    proposalDate: "",
    enterpriseNumber: "2025/875945/07",
    registeredAddress: "",
    subjectLegalName: "",
    subjectAddress: "",
    idNumber: "",
    words: "60,000 to 70,000",
    commencementDate: "",
    deliveryDate: "",
    copies: "sixty",
    additionalCopyPrice: "R1,500",
    totalFee: "R1,024,000",
    feeInWords: "one million and twenty-four thousand rand",
    instalment: "R256,000",
    q2Date: "",
    q3Date: "",
    pages: "200 to 240",
    images: "40 to 60",
    subjectInterviews: "fourteen (14)",
    circleInterviews: "sixteen (16)",
    revisions: "Three",
    structureNote: "written across a four-season structure",
  });
  useEffect(() => {
    setFields((f) => ({ ...f, ...applyMap(profile, AGREEMENT_MAP) }));
  }, [profile]);
  const html = React.useMemo(() => buildServiceAgreementHtml(fields), [fields]);
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
  const baseName =
    "service-agreement" +
    (fields.subjectLegalName.trim() ? `-${slug(fields.subjectLegalName)}` : "");

  const grp = "text-[12px] uppercase tracking-[0.28em] text-[#1C1A17] mb-4 pb-2 border-b border-[#A8A296]";
  return (
    <DocumentPanel
      testidPrefix="service-agreement"
      kicker="Legal · Strictly confidential"
      title="The Raven's Quill, Service Agreement"
      description="The binding contract that gives effect to the Life Study proposal, including Appendices A (timeline) and B (scope and deliverables). Complete the parties, dates, and figures; the standard twelve-month timeline is fixed. Have it reviewed by a South African attorney before first use."
      html={html}
      baseName={baseName}
      signable
      docType="service-agreement"
    >
      <div className="space-y-8">
        <div>
          <h3 className={grp}>The commission &amp; parties</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="sa-projectTitle" label="Project title" value={fields.projectTitle} onChange={set("projectTitle")} placeholder="Four Seasons in One Day" testid="service-agreement-project-title" />
            <Field id="sa-proposalDate" label="Proposal date" value={fields.proposalDate} onChange={set("proposalDate")} placeholder="7 June 2026" testid="service-agreement-proposal-date" />
            <Field id="sa-enterpriseNumber" label="Enterprise number" value={fields.enterpriseNumber} onChange={set("enterpriseNumber")} placeholder="2025/875945/07" testid="service-agreement-enterprise-number" />
            <Field id="sa-registeredAddress" label="Registered address" value={fields.registeredAddress} onChange={set("registeredAddress")} placeholder="Registered company address" testid="service-agreement-registered-address" />
            <Field id="sa-subjectLegalName" label="Subject's full legal name" value={fields.subjectLegalName} onChange={set("subjectLegalName")} placeholder="Full legal name" testid="service-agreement-subject-legal-name" />
            <Field id="sa-idNumber" label="Subject's ID number" value={fields.idNumber} onChange={set("idNumber")} placeholder="Identity number" testid="service-agreement-id-number" />
            <Field id="sa-subjectAddress" label="Subject's address" value={fields.subjectAddress} onChange={set("subjectAddress")} placeholder="Subject's address" testid="service-agreement-subject-address" wide />
          </div>
        </div>

        <div>
          <h3 className={grp}>Dates &amp; the work</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="sa-commencementDate" label="Commencement date" value={fields.commencementDate} onChange={set("commencementDate")} placeholder="1 January 2027" testid="service-agreement-commencement-date" />
            <Field id="sa-deliveryDate" label="Delivery date" value={fields.deliveryDate} onChange={set("deliveryDate")} placeholder="On or about 31 December 2027" testid="service-agreement-delivery-date" />
            <Field id="sa-words" label="Word count" value={fields.words} onChange={set("words")} placeholder="60,000 to 70,000" testid="service-agreement-words" />
            <Field id="sa-pages" label="Pages" value={fields.pages} onChange={set("pages")} placeholder="200 to 240" testid="service-agreement-pages" />
            <Field id="sa-images" label="Photographs" value={fields.images} onChange={set("images")} placeholder="40 to 60" testid="service-agreement-images" />
            <Field id="sa-copies" label="Bound copies (heritage run)" value={fields.copies} onChange={set("copies")} placeholder="sixty" testid="service-agreement-copies" />
            <Field id="sa-subjectInterviews" label="Interviews with the Subject" value={fields.subjectInterviews} onChange={set("subjectInterviews")} placeholder="fourteen (14)" testid="service-agreement-subject-interviews" />
            <Field id="sa-circleInterviews" label="Interviews with the circle" value={fields.circleInterviews} onChange={set("circleInterviews")} placeholder="sixteen (16)" testid="service-agreement-circle-interviews" />
            <Field id="sa-revisions" label="Revision rounds" value={fields.revisions} onChange={set("revisions")} placeholder="Three" testid="service-agreement-revisions" />
            <Field id="sa-structureNote" label="Structure note" value={fields.structureNote} onChange={set("structureNote")} placeholder="written across a four-season structure" testid="service-agreement-structure-note" wide />
          </div>
        </div>

        <div>
          <h3 className={grp}>Fee &amp; payment</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="sa-totalFee" label="Total fee" value={fields.totalFee} onChange={set("totalFee")} placeholder="R1,024,000" testid="service-agreement-total-fee" />
            <Field id="sa-feeInWords" label="Fee in words" value={fields.feeInWords} onChange={set("feeInWords")} placeholder="one million and twenty-four thousand rand" testid="service-agreement-fee-in-words" />
            <Field id="sa-instalment" label="Per instalment (equal, x4)" value={fields.instalment} onChange={set("instalment")} placeholder="R256,000" testid="service-agreement-instalment" />
            <Field id="sa-additionalCopyPrice" label="Additional copy price" value={fields.additionalCopyPrice} onChange={set("additionalCopyPrice")} placeholder="R1,500" testid="service-agreement-additional-copy-price" />
            <Field id="sa-q2Date" label="Instalment 2 due (end of Q1)" value={fields.q2Date} onChange={set("q2Date")} placeholder="1 April 2027" testid="service-agreement-q2-date" />
            <Field id="sa-q3Date" label="Instalment 3 due (end of Q2)" value={fields.q3Date} onChange={set("q3Date")} placeholder="1 July 2027" testid="service-agreement-q3-date" />
          </div>
        </div>
      </div>
    </DocumentPanel>
  );
};

/* --------------------- An Anthropological Biography, Proposal --------------------- */
// The personal passages are pre-filled with the Nthobi proposal wording as an
// example to overwrite. The structural spine and figures live in the generator.
const PROPOSAL_LETTER_EXAMPLE = `You said it first, not me.

I asked how the weather was, and you said, the way you say everything, as though it were obvious and slightly beneath you to have to explain it, that Cape Town can give you four seasons in one day, but maybe that, maybe that is the actual appeal.

I have spent forty years watching you do the thing you had just described the weather doing: arriving as one person before lunch and another by the time the light changed. Four seasons in one day is not a complaint about Cape Town. It is the most accurate description of your life that anyone, including you, has ever produced, and you produced it by accident, while answering a question about the weather.

I want to write your biography. Not as a hired hand who disappears into your voice and hands you a book that pretends I was never in the room, but as what I actually am: a social anthropologist, looking hard at a life I have stood close to, and writing it down with you, across the year it takes to understand a life fully.

I want the finished book in your hands as a bound heirloom, and a copy printed for each year you have lived, to give to the people you choose. This document sets out exactly how, what it would cost, and the one promise I will not compress: your time to read what I write.`;

const PROPOSAL_SHAPE_EXAMPLE = `Four Seasons in One Day, a life told in weather rather than chronology, because Cape Town is not chronological and neither are you. These are weather fronts, not chapters, and which of them gathers into a chapter is something the fieldwork and your own choosing will decide.

Summer. The years of heat and arena: a political life and the liberation struggle, and the corporate chapter that followed.

Autumn. Love, and its weather: raising a daughter alone, a short later marriage that did not hold, and the leaving of Johannesburg.

Winter. Solitude, reflection, the late-night musings. Aging out loud, and the trauma there was finally time to name.

Spring. The garden, the grandchildren, being in love with your own life. Not arrival, but the season where you decide the survival was worth telling.

A starting shape, not a verdict. The fieldwork will overturn part of it, and the overturning is itself data.`;

const PROPOSAL_CLOSING_EXAMPLE = `You have written, more than once, that the survival was worth telling, and the record worth leaving.

I would like your life set down in full, bound as an heirloom, and a copy for each year you have lived to hand to the people who have walked it with you.

I am not trying to take the telling from anyone. I am offering to do it now, while the seasons are still turning fast enough to be interesting, and to do it the only honest way I know: not by pretending to be you, but by sitting beside you, looking hard, and writing down what an anthropologist can see.

Let us make the book, the true one, with both of us in it.`;

const BiographyProposalDoc = ({ profile }) => {
  const [fields, setFields] = useState({
    workingTitle: "Four Seasons in One Day",
    pitchLine:
      "The life of Nthobi Angel. I want to write your biography, with you, using the methods of anthropology, and to place it in your hands as a bound heirloom.",
    subjectName: "Nthobi Angel",
    subjectLocation: "Cape Town",
    subjectFullName: "Nthobi Angel Moahloli",
    term: "Approximately twelve months, to a delivery of your choosing",
    proposalDate: formatToday(),
    letterBody: PROPOSAL_LETTER_EXAMPLE,
    shapeBody: PROPOSAL_SHAPE_EXAMPLE,
    closingBody: PROPOSAL_CLOSING_EXAMPLE,
    pages: "200 to 240",
    words: "60,000 to 70,000",
    images: "40 to 60",
    printRun: "sixty",
    editionName: "The Life Study",
    fee: "R1,024,000",
    feeTerm: "Twelve months, only a few Life Studies at a time",
    instalmentCount: "four",
    instalment: "R256,000",
  });
  useEffect(() => {
    setFields((f) => ({ ...f, ...applyMap(profile, PROPOSAL_MAP) }));
  }, [profile]);
  const html = React.useMemo(() => buildBiographyProposalHtml(fields), [fields]);
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
  const baseName =
    "biography-proposal" + (fields.subjectName.trim() ? `-${slug(fields.subjectName)}` : "");

  return (
    <DocumentPanel
      testidPrefix="proposal-bio"
      kicker="Client · Confidential"
      title="An Anthropological Biography, Proposal"
      description="The full, first-person commission proposal, modelled on the Nthobi proposal and generalised for any subject. The structural spine (method, deliverables, fee, authorship, references) is fixed; the personal passages below are pre-filled with the Nthobi wording as an example to overwrite. Every figure is a field."
      html={html}
      baseName={baseName}
      signable
      docType="biography-proposal"
    >
      <div className="space-y-8">
        <div>
          <h3 className="text-[12px] uppercase tracking-[0.28em] text-[#1C1A17] mb-4 pb-2 border-b border-[#A8A296]">Cover &amp; subject</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="workingTitle" label="Working title" value={fields.workingTitle} onChange={set("workingTitle")} placeholder="Four Seasons in One Day" testid="proposal-bio-working-title" />
            <Field id="subjectName" label="Subject name" value={fields.subjectName} onChange={set("subjectName")} placeholder="Nthobi Angel" testid="proposal-bio-subject-name" />
            <Field id="subjectLocation" label="Subject location" value={fields.subjectLocation} onChange={set("subjectLocation")} placeholder="Cape Town" testid="proposal-bio-subject-location" />
            <Field id="subjectFullName" label="Subject full name (agreement)" value={fields.subjectFullName} onChange={set("subjectFullName")} placeholder="Nthobi Angel Moahloli" testid="proposal-bio-subject-full-name" />
            <Field id="term" label="Term" value={fields.term} onChange={set("term")} placeholder="Eight months, to a launch on 28 March 2027" testid="proposal-bio-term" wide />
            <Field id="proposalDate" label="Date" value={fields.proposalDate} onChange={set("proposalDate")} placeholder="7 June 2026" testid="proposal-bio-date" />
            <Field id="pitchLine" label="One-line pitch" value={fields.pitchLine} onChange={set("pitchLine")} placeholder="The life of [subject]. I want to write your biography, with you..." testid="proposal-bio-pitch" wide />
          </div>
        </div>

        <div>
          <h3 className="text-[12px] uppercase tracking-[0.28em] text-[#1C1A17] mb-4 pb-2 border-b border-[#A8A296]">Personal passages (overwrite the example)</h3>
          <div className="grid grid-cols-1 gap-5">
            <TextArea id="letterBody" label="Opening letter" value={fields.letterBody} onChange={set("letterBody")} testid="proposal-bio-letter" rows={10} />
            <TextArea id="shapeBody" label="The shape of the book" value={fields.shapeBody} onChange={set("shapeBody")} testid="proposal-bio-shape" rows={8} />
            <TextArea id="closingBody" label="Closing note" value={fields.closingBody} onChange={set("closingBody")} testid="proposal-bio-closing" rows={8} />
          </div>
        </div>

        <div>
          <h3 className="text-[12px] uppercase tracking-[0.28em] text-[#1C1A17] mb-4 pb-2 border-b border-[#A8A296]">The object</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="pages" label="Pages" value={fields.pages} onChange={set("pages")} placeholder="200 to 240" testid="proposal-bio-pages" />
            <Field id="words" label="Word count" value={fields.words} onChange={set("words")} placeholder="60,000 to 70,000" testid="proposal-bio-words" />
            <Field id="images" label="Photographs" value={fields.images} onChange={set("images")} placeholder="40 to 60" testid="proposal-bio-images" />
            <Field id="printRun" label="Print run (copies)" value={fields.printRun} onChange={set("printRun")} placeholder="seventy-three" testid="proposal-bio-print-run" />
          </div>
        </div>

        <div>
          <h3 className="text-[12px] uppercase tracking-[0.28em] text-[#1C1A17] mb-4 pb-2 border-b border-[#A8A296]">The fee &amp; payment</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="editionName" label="Edition name" value={fields.editionName} onChange={set("editionName")} placeholder="The Life Study" testid="proposal-bio-edition-name" />
            <Field id="fee" label="Full investment" value={fields.fee} onChange={set("fee")} placeholder="R1,024,000" testid="proposal-bio-fee" />
            <Field id="feeTerm" label="Fee term line" value={fields.feeTerm} onChange={set("feeTerm")} placeholder="Twelve months, only a few Life Studies at a time" testid="proposal-bio-fee-term" />
            <Field id="instalmentCount" label="Number of instalments (word)" value={fields.instalmentCount} onChange={set("instalmentCount")} placeholder="four" testid="proposal-bio-instalment-count" />
            <Field id="instalment" label="Per instalment (equal)" value={fields.instalment} onChange={set("instalment")} placeholder="R256,000" testid="proposal-bio-instalment" />
          </div>
        </div>
      </div>
    </DocumentPanel>
  );
};

/* ------------------- The Life Study, Commission Prospectus ------------------- */
// House-standard full commission. Loaded on demand; the subject-specific
// identity fields (names) are left untouched so they are always entered by hand.
const LIFE_STUDY_STANDARD = {
  date: formatToday(),
  conversationsCount: "thirty",
  conversationsPeriod: "six months",
  knownArchive:
    "the family's private papers, correspondence, official documents, and photographic collections",
  residencyLocation: "the subject's home",
  residencyPeriod: "one month",
  interviewsCount: "twelve",
  interviewsCircle: "family, close friends, former colleagues, and community figures",
  includedVolumes: "sixty",
  additionalCopyPrice: "R1,500",
  investment: "R1,024,000",
  instalment: "R256,000",
};

const LifeStudyProspectusDoc = ({ profile }) => {
  const [fields, setFields] = useState({
    familyName: "",
    subjectFullName: "",
    subjectFirstName: "",
    date: formatToday(),
    conversationsCount: "",
    conversationsPeriod: "",
    knownArchive: "",
    residencyLocation: "",
    residencyPeriod: "",
    interviewsCount: "",
    interviewsCircle: "",
    includedVolumes: "",
    additionalCopyPrice: "R1,500",
    investment: "",
    instalment: "",
  });
  useEffect(() => {
    setFields((f) => ({ ...f, ...applyMap(profile, PROSPECTUS_MAP) }));
  }, [profile]);
  const html = React.useMemo(() => buildLifeStudyProspectusHtml(fields), [fields]);
  const packetHtml = React.useMemo(() => buildLifeStudyPacketHtml(fields), [fields]);
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
  const loadStandard = () => setFields((f) => ({ ...f, ...LIFE_STUDY_STANDARD }));
  const baseName =
    "life-study-prospectus" +
    (fields.subjectFullName.trim() ? `-${slug(fields.subjectFullName)}` : "");
  const packetBaseName =
    "life-study-prospectus-and-sample" +
    (fields.subjectFullName.trim() ? `-${slug(fields.subjectFullName)}` : "");

  return (
    <DocumentPanel
      testidPrefix="life-study"
      kicker="Client · Confidential"
      title="The Life Study, Commission Prospectus"
      description="The full commission prospectus for a Life Study, personalised to a single life. Fill in the subject, the scope of the fieldwork, the heritage run, and the investment. Blank fields show a bracketed placeholder. It goes out with a writing sample, so the one-tap packet download combines both into a single PDF."
      html={html}
      baseName={baseName}
      packetHtml={packetHtml}
      packetBaseName={packetBaseName}
    >
      <div className="flex items-center gap-4 border border-[#A8A296] bg-white/40 px-5 py-4">
        <button
          type="button"
          className={BTN_CLASS}
          data-testid="life-study-load-standard"
          onClick={loadStandard}
        >
          Load standard commission
        </button>
        <span className="text-[12px] italic text-[#6E6A5E]">
          Fills the scope and figures with the house-standard full commission. The names stay yours to enter, and every field remains editable afterward.
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field id="familyName" label="Prepared for (client / family)" value={fields.familyName} onChange={set("familyName")} placeholder="The Mokoena family" testid="life-study-family-name" />
        <Field id="subjectFullName" label="Subject's full name" value={fields.subjectFullName} onChange={set("subjectFullName")} placeholder="Full name of the subject" testid="life-study-subject-full-name" />
        <Field id="subjectFirstName" label="Subject's first name" value={fields.subjectFirstName} onChange={set("subjectFirstName")} placeholder="First name" testid="life-study-subject-first-name" />
        <Field id="date" label="Date" value={fields.date} onChange={set("date")} placeholder="12 June 2026" testid="life-study-date" />
        <Field id="conversationsCount" label="Conversations (count)" value={fields.conversationsCount} onChange={set("conversationsCount")} placeholder="e.g. thirty" testid="life-study-conversations-count" />
        <Field id="conversationsPeriod" label="Conversations (period)" value={fields.conversationsPeriod} onChange={set("conversationsPeriod")} placeholder="e.g. six months" testid="life-study-conversations-period" />
        <Field id="knownArchive" label="Known archive" value={fields.knownArchive} onChange={set("knownArchive")} placeholder="Correspondence from the 1950s, business records, photographs held at the family home" testid="life-study-known-archive" wide />
        <Field id="residencyLocation" label="Residency location" value={fields.residencyLocation} onChange={set("residencyLocation")} placeholder="e.g. the family home in Polokwane" testid="life-study-residency-location" />
        <Field id="residencyPeriod" label="Residency period" value={fields.residencyPeriod} onChange={set("residencyPeriod")} placeholder="e.g. one concentrated month" testid="life-study-residency-period" />
        <Field id="interviewsCount" label="Triangulation interviews (count)" value={fields.interviewsCount} onChange={set("interviewsCount")} placeholder="e.g. twelve" testid="life-study-interviews-count" />
        <Field id="interviewsCircle" label="The circle interviewed" value={fields.interviewsCircle} onChange={set("interviewsCircle")} placeholder="Three generations of family, former colleagues, and community figures" testid="life-study-interviews-circle" wide />
        <Field id="includedVolumes" label="Included volumes (number)" value={fields.includedVolumes} onChange={set("includedVolumes")} placeholder="e.g. sixty-four" testid="life-study-included-volumes" />
        <Field id="additionalCopyPrice" label="Additional copy price" value={fields.additionalCopyPrice} onChange={set("additionalCopyPrice")} placeholder="R1,500" testid="life-study-additional-copy-price" />
        <Field id="investment" label="The investment (total)" value={fields.investment} onChange={set("investment")} placeholder="R1,024,000" testid="life-study-investment" />
        <Field id="instalment" label="Per instalment (of four)" value={fields.instalment} onChange={set("instalment")} placeholder="R256,000" testid="life-study-instalment" />
      </div>
    </DocumentPanel>
  );
};

/* ---------------- The Life Study, Organisational Prospectus ---------------- */
const OrgProspectusDoc = ({ profile }) => {
  const [fields, setFields] = useState({
    organisationName: "",
    attention: "",
    organisationAddress: "",
    date: formatToday(),
  });
  useEffect(() => {
    setFields((f) => ({ ...f, ...applyMap(profile, ORG_PROSPECTUS_MAP) }));
  }, [profile]);
  const html = React.useMemo(() => buildOrgProspectusHtml(fields), [fields]);
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
  const baseName =
    "organisational-prospectus" +
    (fields.organisationName.trim() ? `-${slug(fields.organisationName)}` : "");

  return (
    <DocumentPanel
      testidPrefix="org-prospectus"
      kicker="Organisation · Confidential"
      title="The Life Study, An Introduction and Invitation"
      description="An introductory prospectus to send to an organisation, introducing the practice and inviting a conversation about a founder, chief executive, chair, or long-serving figure whose life is worth preserving. Address it to a named contact (usually the Head of Human Resources). Enclose a writing sample alongside it."
      html={html}
      baseName={baseName}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field id="org-organisationName" label="Prepared for (organisation)" value={fields.organisationName} onChange={set("organisationName")} placeholder="Acme Holdings" testid="org-prospectus-organisation-name" wide />
        <Field id="org-attention" label="For the attention of" value={fields.attention} onChange={set("attention")} placeholder="Ms. Naledi Khumalo, Head of Human Resources" testid="org-prospectus-attention" wide />
        <Field id="org-organisationAddress" label="Organisation address" value={fields.organisationAddress} onChange={set("organisationAddress")} placeholder="1 Commissioner Street, Sandton, Johannesburg" testid="org-prospectus-organisation-address" wide />
        <Field id="org-date" label="Date" value={fields.date} onChange={set("date")} placeholder="18 July 2026" testid="org-prospectus-date" />
      </div>
    </DocumentPanel>
  );
};

/* --------------- The Life Study, Optional Extensions Prospectus --------------- */
const EXT_PROSPECTUS_DEFAULTS = {
  familyName: "",
  subjectFullName: "",
  date: formatToday(),
  documentaryLength: "ninety minutes",
  documentaryCopies: "ten",
  documentaryNote: "",
  documentaryInvestment: EXTENSION_PRICES.documentary,
  sonicFormat: "a secured digital archive and a bound index of its contents",
  sonicNote: "",
  sonicInvestment: EXTENSION_PRICES.sonic,
  vaultNote: "",
  vaultInvestment: EXTENSION_PRICES.vault,
  allThreeInvestment: EXTENSION_PRICES.allThree,
  lifeStudyFee: "R1,024,000",
  additionalVolumes: "Thirty",
  additionalCopyPrice: "R1,500",
  additionalVolumesTotal: "R45,000",
  totalCommission: "R1,404,000",
};

// The extension investment figures at their house standard, for the one-tap
// "Load standard extensions" reset (mirrors "Load standard commission").
const EXT_PROSPECTUS_STANDARD = {
  documentaryInvestment: EXTENSION_PRICES.documentary,
  sonicInvestment: EXTENSION_PRICES.sonic,
  vaultInvestment: EXTENSION_PRICES.vault,
  allThreeInvestment: EXTENSION_PRICES.allThree,
};

const ExtensionsProspectusDoc = ({ profile }) => {
  const [fields, setFields] = useState({ ...EXT_PROSPECTUS_DEFAULTS });
  useEffect(() => {
    setFields((f) => ({ ...f, ...applyMap(profile, EXT_PROSPECTUS_MAP) }));
  }, [profile]);
  const html = React.useMemo(() => buildExtensionsProspectusHtml(fields), [fields]);
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
  const baseName =
    "extensions-prospectus" +
    (fields.subjectFullName.trim() ? `-${slug(fields.subjectFullName)}` : "");

  return (
    <DocumentPanel
      testidPrefix="ext-prospectus"
      kicker="Client · Confidential"
      title="The Life Study, Optional Extensions Prospectus"
      description="Beyond the Book. The companion prospectus for the optional extensions (the documentary, the sonic archive, the digital vault), issued when a commission reaches beyond the page. Standard house pricing is prefilled and fully editable."
      html={html}
      baseName={baseName}
    >
      <div className="space-y-8">
        <div className="flex items-center gap-4 border border-[#7A2E2E]/40 bg-white/40 px-5 py-4">
          <button type="button" className={BTN_CLASS} data-testid="ext-prospectus-load-standard" onClick={() => setFields((f) => ({ ...f, ...EXT_PROSPECTUS_STANDARD }))}>
            Load standard extensions
          </button>
          <span className="text-[12px] italic text-[#6E6A5E]">
            One tap drops the full standard extended quote (Documentary from R220,000, Sonic Archive R70,000, Digital Vault R45,000, all three from R335,000) into the figures below. Every amount stays editable, and matches the Extensions Invoice.
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field id="ext-pros-familyName" label="Prepared for (client / family)" value={fields.familyName} onChange={set("familyName")} placeholder="The Mokoena family" testid="ext-prospectus-family-name" />
          <Field id="ext-pros-subjectFullName" label="Subject's full name" value={fields.subjectFullName} onChange={set("subjectFullName")} placeholder="Full name of the subject" testid="ext-prospectus-subject-full-name" />
          <Field id="ext-pros-date" label="Date" value={fields.date} onChange={set("date")} placeholder="12 June 2026" testid="ext-prospectus-date" />
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-[0.28em] text-[#1C1A17] mb-4">The Documentary</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="ext-pros-docLength" label="Approximate length" value={fields.documentaryLength} onChange={set("documentaryLength")} placeholder="ninety minutes" testid="ext-prospectus-documentary-length" />
            <Field id="ext-pros-docCopies" label="Private copies for the family" value={fields.documentaryCopies} onChange={set("documentaryCopies")} placeholder="ten" testid="ext-prospectus-documentary-copies" />
            <Field id="ext-pros-docInvestment" label="Investment (from)" value={fields.documentaryInvestment} onChange={set("documentaryInvestment")} placeholder="R220,000" testid="ext-prospectus-documentary-investment" />
            <Field id="ext-pros-docNote" label="Festival / broadcast note (optional)" value={fields.documentaryNote} onChange={set("documentaryNote")} placeholder="Or leave as a private film." testid="ext-prospectus-documentary-note" wide />
          </div>
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-[0.28em] text-[#1C1A17] mb-4">The Sonic Archive</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="ext-pros-sonicFormat" label="Delivery format" value={fields.sonicFormat} onChange={set("sonicFormat")} placeholder="a secured digital archive and a bound index" testid="ext-prospectus-sonic-format" wide />
            <Field id="ext-pros-sonicInvestment" label="Investment" value={fields.sonicInvestment} onChange={set("sonicInvestment")} placeholder="R70,000" testid="ext-prospectus-sonic-investment" />
            <Field id="ext-pros-sonicNote" label="Highlights note (optional)" value={fields.sonicNote} onChange={set("sonicNote")} placeholder="If a short listening edition is included." testid="ext-prospectus-sonic-note" />
          </div>
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-[0.28em] text-[#1C1A17] mb-4">The Digital Vault</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="ext-pros-vaultInvestment" label="Investment" value={fields.vaultInvestment} onChange={set("vaultInvestment")} placeholder="R45,000" testid="ext-prospectus-vault-investment" />
            <Field id="ext-pros-vaultNote" label="Storage / access note (optional)" value={fields.vaultNote} onChange={set("vaultNote")} placeholder="The storage and access arrangement agreed." testid="ext-prospectus-vault-note" />
          </div>
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-[0.28em] text-[#1C1A17] mb-4">Summary &amp; illustration figures</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="ext-pros-allThree" label="All three extensions (from)" value={fields.allThreeInvestment} onChange={set("allThreeInvestment")} placeholder="R335,000" testid="ext-prospectus-all-three-investment" />
            <Field id="ext-pros-lifeStudyFee" label="The Life Study fee" value={fields.lifeStudyFee} onChange={set("lifeStudyFee")} placeholder="R1,024,000" testid="ext-prospectus-life-study-fee" />
            <Field id="ext-pros-additionalVolumes" label="Additional volumes (count)" value={fields.additionalVolumes} onChange={set("additionalVolumes")} placeholder="Thirty" testid="ext-prospectus-additional-volumes" />
            <Field id="ext-pros-additionalCopyPrice" label="Additional copy price (each)" value={fields.additionalCopyPrice} onChange={set("additionalCopyPrice")} placeholder="R1,500" testid="ext-prospectus-additional-copy-price" />
            <Field id="ext-pros-additionalVolumesTotal" label="Additional volumes (total)" value={fields.additionalVolumesTotal} onChange={set("additionalVolumesTotal")} placeholder="R45,000" testid="ext-prospectus-additional-volumes-total" />
            <Field id="ext-pros-totalCommission" label="Total commission (from)" value={fields.totalCommission} onChange={set("totalCommission")} placeholder="R1,404,000" testid="ext-prospectus-total-commission" />
          </div>
        </div>
      </div>
    </DocumentPanel>
  );
};

/* --------------------------- The Life Study, Invoice --------------------------- */
const LifeStudyInvoiceDoc = ({ profile }) => {
  const [rand] = useState(() => String(Math.floor(Math.random() * 900) + 100));
  const [numberTouched, setNumberTouched] = useState(false);
  const [fields, setFields] = useState({
    billedTo: "",
    billedCity: "",
    invoiceNumber: "",
    invoiceDate: formatToday(),
    dueDate: "",
    projectTitle: "",
    agreementDate: "",
    instalmentNumber: "1",
    totalFee: "R1,024,000",
    description: "",
    commencementDate: "",
    q2Date: "",
    q3Date: "",
    deliveryDate: "",
    amountReceived: "",
  });
  useEffect(() => {
    setFields((f) => ({ ...f, ...applyMap(profile, LS_INVOICE_MAP) }));
  }, [profile]);
  const html = React.useMemo(() => buildLifeStudyInvoiceHtml(fields), [fields]);
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));

  // Auto-generate the invoice number from the subject's initials + LS + year +
  // a fixed random suffix, until the admin edits it.
  useEffect(() => {
    if (numberTouched) return;
    const initials = getInitials(fields.billedTo);
    const num = initials ? `${initials}-LS-${new Date().getFullYear()}-${rand}` : "";
    setFields((f) => (f.invoiceNumber === num ? f : { ...f, invoiceNumber: num }));
  }, [fields.billedTo, numberTouched, rand]);

  const baseName =
    "life-study-invoice" + (fields.invoiceNumber.trim() ? `-${slug(fields.invoiceNumber)}` : "");

  return (
    <DocumentPanel
      testidPrefix="ls-invoice"
      kicker="Client · Confidential"
      title="The Life Study, Invoice"
      description="An instalment invoice under the service agreement. Choose which of the four instalments this is; the amount is computed as a quarter of the total fee. Blank fields show a bracketed placeholder."
      html={html}
      baseName={baseName}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={LABEL_CLASS} htmlFor="ls-instalmentNumber">Instalment</label>
          <select
            id="ls-instalmentNumber"
            value={fields.instalmentNumber}
            onChange={set("instalmentNumber")}
            className={INPUT_CLASS}
            data-testid="ls-invoice-instalment-number"
          >
            <option value="1">1 of 4 — commencement</option>
            <option value="2">2 of 4 — second quarter</option>
            <option value="3">3 of 4 — third quarter</option>
            <option value="4">4 of 4 — on delivery</option>
            <option value="full">Full commission — paid upfront</option>
          </select>
        </div>
        <Field id="ls-totalFee" label="Total fee (÷4 per instalment)" value={fields.totalFee} onChange={set("totalFee")} placeholder="R1,024,000" testid="ls-invoice-total-fee" />
        <Field id="ls-amountReceived" label="Amount already received (optional override)" value={fields.amountReceived} onChange={set("amountReceived")} placeholder="Blank = assume prior instalments paid" testid="ls-invoice-amount-received" />
        <Field id="ls-billedTo" label="Billed to (subject)" value={fields.billedTo} onChange={set("billedTo")} placeholder="Full legal name" testid="ls-invoice-billed-to" />
        <Field id="ls-billedCity" label="City, province" value={fields.billedCity} onChange={set("billedCity")} placeholder="Cape Town, Western Cape" testid="ls-invoice-billed-city" />
        <Field id="ls-projectTitle" label="Project title" value={fields.projectTitle} onChange={set("projectTitle")} placeholder="Four Seasons in One Day" testid="ls-invoice-project-title" />
        <Field id="ls-agreementDate" label="Agreement dated" value={fields.agreementDate} onChange={set("agreementDate")} placeholder="7 June 2026" testid="ls-invoice-agreement-date" />
        <Field id="ls-invoiceNumber" label="Invoice number (auto)" value={fields.invoiceNumber} onChange={(e) => { setNumberTouched(true); setFields((f) => ({ ...f, invoiceNumber: e.target.value })); }} placeholder="e.g. NA-LS-2026-482" testid="ls-invoice-number" />
        <Field id="ls-invoiceDate" label="Invoice date" value={fields.invoiceDate} onChange={set("invoiceDate")} placeholder="18 June 2026" testid="ls-invoice-date" />
        <Field id="ls-dueDate" label="Due date" value={fields.dueDate} onChange={set("dueDate")} placeholder="On or before commencement" testid="ls-invoice-due-date" />
        <Field id="ls-commencementDate" label="Schedule: commencement date" value={fields.commencementDate} onChange={set("commencementDate")} placeholder="1 January 2027" testid="ls-invoice-commencement-date" />
        <Field id="ls-q2Date" label="Schedule: second-quarter date" value={fields.q2Date} onChange={set("q2Date")} placeholder="1 April 2027" testid="ls-invoice-q2-date" />
        <Field id="ls-q3Date" label="Schedule: third-quarter date" value={fields.q3Date} onChange={set("q3Date")} placeholder="1 July 2027" testid="ls-invoice-q3-date" />
        <Field id="ls-deliveryDate" label="Schedule: delivery date" value={fields.deliveryDate} onChange={set("deliveryDate")} placeholder="On or about 31 December 2027" testid="ls-invoice-delivery-date" />
        <TextArea id="ls-description" label="Line description (blank = standard wording for the chosen instalment)" value={fields.description} onChange={set("description")} placeholder={LS_INSTALMENTS[fields.instalmentNumber]?.desc || ""} testid="ls-invoice-description" rows={4} />
      </div>
    </DocumentPanel>
  );
};

/* --------------------------- The Life Study, Extensions Invoice --------------------------- */
const ExtensionsInvoiceDoc = ({ profile }) => {
  const [rand] = useState(() => String(Math.floor(Math.random() * 900) + 100));
  const [numberTouched, setNumberTouched] = useState(false);
  const [fields, setFields] = useState({
    billedTo: "",
    billedCity: "",
    invoiceNumber: "",
    invoiceDate: formatToday(),
    dueDate: "",
    projectTitle: "",
    agreementDate: "",
    depositNote: "",
  });
  const [items, setItems] = useState([{ ...EXT_PRESETS.documentary }]);
  useEffect(() => {
    setFields((f) => ({ ...f, ...applyMap(profile, EXT_INVOICE_MAP) }));
  }, [profile]);
  const html = React.useMemo(
    () => buildExtensionsInvoiceHtml({ ...fields, items }),
    [fields, items]
  );
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (i, k) => (e) =>
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: e.target.value } : it)));
  const addItem = (preset) => setItems((arr) => [...arr, { ...EXT_PRESETS[preset] }]);
  const addAllThree = () =>
    setItems([{ ...EXT_PRESETS.documentary }, { ...EXT_PRESETS.sonic }, { ...EXT_PRESETS.vault }]);
  const removeItem = (i) => setItems((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  useEffect(() => {
    if (numberTouched) return;
    const initials = getInitials(fields.billedTo);
    const num = initials ? `${initials}-EX-${new Date().getFullYear()}-${rand}` : "";
    setFields((f) => (f.invoiceNumber === num ? f : { ...f, invoiceNumber: num }));
  }, [fields.billedTo, numberTouched, rand]);

  const baseName =
    "extensions-invoice" + (fields.invoiceNumber.trim() ? `-${slug(fields.invoiceNumber)}` : "");

  return (
    <DocumentPanel
      testidPrefix="ext-invoice"
      kicker="Client · Confidential"
      title="The Life Study, Extensions Invoice"
      description="A standalone invoice for the optional extensions (the documentary, the sonic archive, the digital vault, or bespoke additions), quoted separately from the Life Study fee. Add any number of lines; the total sums automatically."
      html={html}
      baseName={baseName}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field id="ext-billedTo" label="Billed to (subject)" value={fields.billedTo} onChange={set("billedTo")} placeholder="Full legal name" testid="ext-invoice-billed-to" />
          <Field id="ext-billedCity" label="City, province" value={fields.billedCity} onChange={set("billedCity")} placeholder="Cape Town, Western Cape" testid="ext-invoice-billed-city" />
          <Field id="ext-projectTitle" label="Project title" value={fields.projectTitle} onChange={set("projectTitle")} placeholder="Four Seasons in One Day" testid="ext-invoice-project-title" />
          <Field id="ext-agreementDate" label="Agreement dated" value={fields.agreementDate} onChange={set("agreementDate")} placeholder="7 June 2026" testid="ext-invoice-agreement-date" />
          <Field id="ext-invoiceNumber" label="Invoice number (auto)" value={fields.invoiceNumber} onChange={(e) => { setNumberTouched(true); setFields((f) => ({ ...f, invoiceNumber: e.target.value })); }} placeholder="e.g. NA-EX-2026-482" testid="ext-invoice-number" />
          <Field id="ext-invoiceDate" label="Invoice date" value={fields.invoiceDate} onChange={set("invoiceDate")} placeholder="18 June 2026" testid="ext-invoice-date" />
          <Field id="ext-dueDate" label="Due date" value={fields.dueDate} onChange={set("dueDate")} placeholder="On acceptance" testid="ext-invoice-due-date" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[12px] uppercase tracking-[0.28em] text-[#1C1A17]">Line items</span>
          </div>
          <div className="flex items-center gap-4 border border-[#7A2E2E]/40 bg-white/40 px-5 py-4 mb-5">
            <button type="button" className={BTN_CLASS} data-testid="ext-invoice-add-all" onClick={addAllThree}>
              All three extensions
            </button>
            <span className="text-[12px] italic text-[#6E6A5E]">
              One tap assembles a full extended commission: the Documentary, the Sonic Archive, and the Digital Vault, each at its standard figure. Replaces the current lines; every amount stays editable.
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            <button type="button" className={SMALL_BTN} data-testid="ext-invoice-add-documentary" onClick={() => addItem("documentary")}>+ Documentary</button>
            <button type="button" className={SMALL_BTN} data-testid="ext-invoice-add-sonic" onClick={() => addItem("sonic")}>+ Sonic archive</button>
            <button type="button" className={SMALL_BTN} data-testid="ext-invoice-add-vault" onClick={() => addItem("vault")}>+ Digital vault</button>
            <button type="button" className={SMALL_BTN} data-testid="ext-invoice-add-custom" onClick={() => addItem("custom")}>+ Custom line</button>
          </div>
          <div className="space-y-4">
            {items.map((it, i) => (
              <div key={i} className="border border-[#A8A296] p-4 bg-white/40" data-testid={`ext-invoice-item-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-1 space-y-3">
                    <Field id={`ext-item-${i}-name`} label={`Line ${i + 1} — name`} value={it.name} onChange={setItem(i, "name")} placeholder="The Documentary" testid={`ext-invoice-item-${i}-name`} />
                    <TextArea id={`ext-item-${i}-desc`} label="Description" value={it.desc} onChange={setItem(i, "desc")} placeholder="A short description of this extension" testid={`ext-invoice-item-${i}-desc`} rows={3} />
                    <Field id={`ext-item-${i}-amount`} label="Amount (ZAR)" value={it.amount} onChange={setItem(i, "amount")} placeholder="e.g. R450,000" testid={`ext-invoice-item-${i}-amount`} />
                  </div>
                  <button type="button" className="text-[11px] uppercase tracking-[0.2em] text-[#7A2E2E] hover:underline mt-7" data-testid={`ext-invoice-item-${i}-remove`} onClick={() => removeItem(i)} disabled={items.length <= 1}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <TextArea id="ext-depositNote" label="Deposit note (optional)" value={fields.depositNote} onChange={set("depositNote")} placeholder="e.g. A 50% deposit is payable on acceptance, with the balance due on delivery." testid="ext-invoice-deposit-note" rows={2} />
      </div>
    </DocumentPanel>
  );
};

/* ------------------------- The Final Tribute, Prospectus ------------------------- */
const TributeProspectusDoc = ({ profile }) => {
  const [fields, setFields] = useState({ familyName: "", deceasedName: "", date: "" });
  useEffect(() => {
    setFields((f) => ({ ...f, ...applyMap(profile, TRIBUTE_PROSPECTUS_MAP) }));
  }, [profile]);
  const html = React.useMemo(() => buildTributeProspectusHtml(fields), [fields]);
  const packetHtml = React.useMemo(() => buildTributePacketHtml(fields), [fields]);
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
  const baseName =
    "final-tribute-prospectus" + (fields.familyName.trim() ? `-${slug(fields.familyName)}` : "");
  const packetBaseName =
    "final-tribute-prospectus-and-sample" + (fields.familyName.trim() ? `-${slug(fields.familyName)}` : "");

  return (
    <DocumentPanel
      testidPrefix="prospectus"
      kicker="Client · Confidential"
      title="The Final Tribute, Prospectus"
      description="A short prospectus sent to a family after the first conversation, setting out what the Final Tribute is, the two forms, the fee, and the terms. It goes out with a writing sample, so the one-tap packet download combines the prospectus and the specimen tribute into a single PDF."
      html={html}
      baseName={baseName}
      packetHtml={packetHtml}
      packetBaseName={packetBaseName}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <Field id="familyName" label="Family name" value={fields.familyName} onChange={set("familyName")} placeholder="The Phoofolo family" testid="prospectus-family-name" />
        <Field id="deceasedName" label="Name of the deceased" value={fields.deceasedName} onChange={set("deceasedName")} placeholder="Full name" testid="prospectus-deceased-name" />
        <Field id="date" label="Date" value={fields.date} onChange={set("date")} placeholder="12 June 2026" testid="prospectus-date" />
      </div>
    </DocumentPanel>
  );
};

/* --------------------------- The Final Tribute, Invoice --------------------------- */
const TributeInvoiceDoc = ({ profile }) => {
  const [rand] = useState(() => String(Math.floor(Math.random() * 900) + 100));
  const [numberTouched, setNumberTouched] = useState(false);
  const [fields, setFields] = useState({
    tributeType: "printed",
    billedTo: "",
    billedCity: "",
    deceasedName: "",
    invoiceNumber: "",
    invoiceDate: formatToday(),
    extraLeaflets: "",
  });
  useEffect(() => {
    setFields((f) => ({ ...f, ...applyMap(profile, TRIBUTE_INVOICE_MAP) }));
  }, [profile]);
  const html = React.useMemo(() => buildTributeInvoiceHtml(fields), [fields]);
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));

  // Auto-generate the invoice number from the commissioning individual's
  // initials + FT + year + a fixed random suffix, until the admin edits it.
  useEffect(() => {
    if (numberTouched) return;
    const initials = getInitials(fields.billedTo);
    const num = initials ? `${initials}-FT-${new Date().getFullYear()}-${rand}` : "";
    setFields((f) => (f.invoiceNumber === num ? f : { ...f, invoiceNumber: num }));
  }, [fields.billedTo, numberTouched, rand]);

  const baseName =
    "final-tribute-invoice" +
    (fields.invoiceNumber.trim() ? `-${slug(fields.invoiceNumber)}` : "");

  return (
    <DocumentPanel
      testidPrefix="invoice"
      kicker="Client · Confidential"
      title="The Final Tribute, Invoice"
      description="A short invoice sent to confirm a Final Tribute commission. Choose the tribute form and the totals compute automatically. Blank fields show a bracketed placeholder."
      html={html}
      baseName={baseName}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={LABEL_CLASS} htmlFor="tributeType">Tribute form</label>
          <select
            id="tributeType"
            value={fields.tributeType}
            onChange={set("tributeType")}
            className={INPUT_CLASS}
            data-testid="invoice-tribute-type"
          >
            <option value="printed">The Printed Tribute (R24,000)</option>
            <option value="written">The Written Tribute (R18,000)</option>
          </select>
        </div>
        <Field id="extraLeaflets" label="Additional leaflets (qty)" value={fields.extraLeaflets} onChange={set("extraLeaflets")} placeholder="0" testid="invoice-extra-leaflets" />
        <Field id="billedTo" label="Billed to" value={fields.billedTo} onChange={set("billedTo")} placeholder="The Mokoena family" testid="invoice-billed-to" />
        <Field id="billedCity" label="Address / city, province" value={fields.billedCity} onChange={set("billedCity")} placeholder="Pretoria, Gauteng" testid="invoice-billed-city" />
        <Field id="deceasedName" label="In tribute to" value={fields.deceasedName} onChange={set("deceasedName")} placeholder="Name of the deceased" testid="invoice-deceased-name" />
        <Field id="invoiceNumber" label="Invoice number (auto)" value={fields.invoiceNumber} onChange={(e) => { setNumberTouched(true); setFields((f) => ({ ...f, invoiceNumber: e.target.value })); }} placeholder="e.g. SM-FT-2026-482" testid="invoice-number" />
        <Field id="invoiceDate" label="Invoice date" value={fields.invoiceDate} onChange={set("invoiceDate")} placeholder="18 June 2026" testid="invoice-date" />
      </div>
    </DocumentPanel>
  );
};

export default AdminDocuments;
